/**
 * SiteSearch — global magnifier button + Cmd+K-style modal + /search results.
 *
 * Client-side fuzzy match over three registries:
 *   1. Every marketplace product (imported from MarketplacePage's PRODUCTS)
 *   2. Every microfiber SKU with its variants (Direct Mop Sales)
 *   3. A curated list of key site pages (marketplace, command centers, contact, etc.)
 *
 * Usage:
 *   <SiteSearchButton />   — the magnifier icon (drop into SiteHeader)
 *   /search?q=microfiber   — full results grid page
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, X, Package, FileText, ShoppingCart } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import { PRODUCTS } from "@/pages/MarketplacePage";

// Small curated list — the pages hospital buyers might search for by name.
const SITE_PAGES = [
  { title: "Marketplace", desc: "Full B2B catalog · gloves, microfiber, sterile processing, and more", to: "/marketplace" },
  { title: "Sterile Processing Command Center", desc: "MicroCare Spec Clean™ · ProChek® · detergents & indicators", to: "/sterile-processing" },
  { title: "Backorder Sourcing", desc: "Hospital supply backorder & discontinued SKU sourcing", to: "/how-it-works/backorder-sourcing" },
  { title: "Dialysis Command Center", desc: "Hemodialysis products, care, Diasol Acid Concentrate", to: "/dialysis" },
  { title: "Water Purification", desc: "Sol Millennium RO, DI tank exchange, Hydronix filters", to: "/water-purification" },
  { title: "Environmental Services (EVS)", desc: "Filmop USA, microfiber, janitorial supplies", to: "/environmental-services" },
  { title: "General Contracting", desc: "Facility capital projects · TX/LA/AL/MS", to: "/general-contracting" },
  { title: "Contact Sterile Mate", desc: "Phone, email, form, Houston HQ map", to: "/contact" },
  { title: "Hospital Login", desc: "Sign in or create a hospital account", to: "/hospital/login" },
  { title: "Careers", desc: "Join the Sterile Mate team", to: "/careers" },
  { title: "About Sterile Mate", desc: "certified small business certified · Texas Medical Center", to: "/about" },
  { title: "Diversity & Inclusion", desc: "Our commitment to equitable procurement", to: "/diversity-inclusion" },
];

function normalize(s) {
  return (s || "").toLowerCase();
}

function scoreProduct(p, q) {
  const name = normalize(p.name);
  const brand = normalize(p.brand);
  const category = normalize(p.category);
  const sub = normalize(p.subcategory);
  const tokens = q.split(/\s+/).filter(Boolean);
  let score = 0;
  for (const t of tokens) {
    if (!t) continue;
    if (name.startsWith(t)) score += 10;
    else if (name.includes(t)) score += 6;
    if (brand.includes(t)) score += 3;
    if (category.includes(t)) score += 2;
    if (sub && sub.includes(t)) score += 2;
  }
  return score;
}

export function searchAll(query, { limit = 25 } = {}) {
  const q = normalize(query).trim();
  if (!q) return { products: [], pages: [] };
  const products = PRODUCTS
    .map((p) => ({ p, s: scoreProduct(p, q) }))
    .filter((r) => r.s > 0)
    .sort((a, b) => b.s - a.s)
    .slice(0, limit)
    .map((r) => r.p);
  const pages = SITE_PAGES
    .filter((pg) => normalize(pg.title).includes(q) || normalize(pg.desc).includes(q))
    .slice(0, 8);
  return { products, pages };
}

// ────────────────────────────────────────────────────────────────
// 1. Header button (magnifier) — opens the modal
// ────────────────────────────────────────────────────────────────
export function SiteSearchButton({ compact = false }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={
          compact
            ? "w-9 h-9 flex items-center justify-center rounded-md text-white/80 hover:text-white hover:bg-white/10"
            : "flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/15 border border-white/15 text-white/80 hover:text-white text-xs font-medium transition-colors"
        }
        aria-label="Search site"
        data-testid="site-search-open"
      >
        <Search className="w-4 h-4" />
        {!compact && (
          <>
            <span className="hidden md:inline">Search</span>
            <kbd className="hidden lg:inline px-1.5 py-0.5 text-[10px] rounded bg-white/15 border border-white/20 font-mono">⌘K</kbd>
          </>
        )}
      </button>
      {open && <SiteSearchModal onClose={() => setOpen(false)} />}
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// 2. Modal (Cmd+K style)
// ────────────────────────────────────────────────────────────────
function SiteSearchModal({ onClose }) {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const results = useMemo(() => searchAll(query, { limit: 8 }), [query]);
  const totalHits = results.products.length + results.pages.length;

  const goToFullResults = () => {
    onClose();
    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && query.trim()) {
      if (results.products.length === 1 && results.pages.length === 0) {
        onClose();
        navigate(`/marketplace/product/${results.products[0].id}`);
      } else {
        goToFullResults();
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/70 backdrop-blur-sm flex items-start justify-center pt-20 px-4" data-testid="site-search-modal" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200">
          <Search className="w-5 h-5 text-slate-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKey}
            placeholder="Search products, categories, catalog pages..."
            className="flex-1 outline-none text-base text-slate-900 placeholder-slate-400"
            data-testid="site-search-input"
          />
          {query && (
            <button onClick={() => setQuery("")} className="text-slate-400 hover:text-slate-700 text-xs" data-testid="site-search-clear">Clear</button>
          )}
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="site-search-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {!query.trim() && (
            <div className="px-5 py-12 text-center text-sm text-slate-500">
              <Package className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              Start typing to search {PRODUCTS.length} products and {SITE_PAGES.length} catalog pages.
            </div>
          )}
          {query.trim() && totalHits === 0 && (
            <div className="px-5 py-12 text-center text-sm text-slate-500">
              No matches for <strong>&quot;{query}&quot;</strong>. Try a category name, brand, or SKU.
            </div>
          )}
          {results.products.length > 0 && (
            <div>
              <p className="px-5 pt-3 pb-1 text-[10px] font-black uppercase tracking-widest text-slate-500">Products ({results.products.length})</p>
              {results.products.map((p) => (
                <Link
                  key={p.id}
                  to={`/marketplace/product/${p.id}`}
                  onClick={onClose}
                  className="flex items-center gap-3 px-5 py-2.5 hover:bg-slate-50 border-t border-slate-100"
                  data-testid={`site-search-hit-${p.id}`}
                >
                  <img src={p.image} alt="" className="w-10 h-10 rounded object-contain bg-slate-100 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{p.name}</p>
                    <p className="text-[11px] text-slate-500 truncate">{p.brand} · {p.subcategory || p.category}</p>
                  </div>
                  <ShoppingCart className="w-4 h-4 text-slate-300 shrink-0" />
                </Link>
              ))}
            </div>
          )}
          {results.pages.length > 0 && (
            <div>
              <p className="px-5 pt-3 pb-1 text-[10px] font-black uppercase tracking-widest text-slate-500">Pages ({results.pages.length})</p>
              {results.pages.map((pg) => (
                <Link
                  key={pg.to}
                  to={pg.to}
                  onClick={onClose}
                  className="flex items-center gap-3 px-5 py-2.5 hover:bg-slate-50 border-t border-slate-100"
                  data-testid={`site-search-page-${pg.to.replace(/[^a-z0-9]/gi,"-")}`}
                >
                  <div className="w-10 h-10 rounded bg-cyan-50 flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-cyan-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{pg.title}</p>
                    <p className="text-[11px] text-slate-500 truncate">{pg.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {query.trim() && totalHits > 0 && (
          <div className="border-t border-slate-200 px-5 py-2.5 bg-slate-50 text-[11px] text-slate-500 flex items-center justify-between">
            <span>Press <kbd className="px-1 py-0.5 rounded bg-white border border-slate-300 font-mono">Enter</kbd> to see all results</span>
            <button onClick={goToFullResults} className="font-bold text-cyan-700 hover:text-cyan-900" data-testid="site-search-see-all">
              See all results →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// 3. Full results page — /search?q=...
// ────────────────────────────────────────────────────────────────
export default function SearchResultsPage() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const results = useMemo(() => searchAll(q, { limit: 60 }), [q]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="site-search-results-page">
      <SiteHeader />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-black text-slate-900 mb-1">Search results</h1>
        <p className="text-slate-500 mb-6">
          {q ? <>Showing matches for <strong>&quot;{q}&quot;</strong> — {results.products.length} products · {results.pages.length} pages</> : "Type in the header search to find products and pages."}
        </p>

        {results.pages.length > 0 && (
          <section className="mb-10">
            <h2 className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-3">Pages</h2>
            <div className="grid md:grid-cols-2 gap-3">
              {results.pages.map((pg) => (
                <Link key={pg.to} to={pg.to} className="block p-4 rounded-xl border border-slate-200 bg-white hover:border-slate-400 hover:shadow-sm transition-all">
                  <p className="text-sm font-bold text-slate-900">{pg.title}</p>
                  <p className="text-xs text-slate-600 mt-0.5">{pg.desc}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {results.products.length > 0 ? (
          <section>
            <h2 className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-3">Products ({results.products.length})</h2>
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
              {results.products.map((p) => (
                <Link key={p.id} to={`/marketplace/product/${p.id}`} className="block rounded-xl border border-slate-200 bg-white overflow-hidden hover:shadow-md hover:border-slate-400 transition-all">
                  <img src={p.image} alt={p.name} className="w-full h-40 object-contain bg-slate-50 p-3" />
                  <div className="p-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 truncate">{p.brand}</p>
                    <p className="text-sm font-semibold text-slate-900 line-clamp-2">{p.name}</p>
                    {Number(p.customer_price) > 0 && (
                      <p className="text-sm font-black text-slate-900 mt-1">${Number(p.customer_price).toFixed(2)}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ) : (
          q && <p className="text-slate-500">No product matches. Try a broader category name or SKU prefix.</p>
        )}
      </div>
    </div>
  );
}
